/**
 * Estado e regras do modal de criar/editar contato — C-75 + GUPM-202.
 *
 * O componente (`ContatoForm`) só desenha; aqui ficam a entrada, a validação do
 * domínio (`validarContato`), as travas (`motivoBloqueio*`) e as gravações.
 *
 * GUPM-202:
 * - vincular a um usuário do app copia o nome e o e-mail dele para o formulário
 *   (somente leitura) e grava como reserva; "Não é usuário do app" libera os campos;
 * - as funções são etiquetas; o texto digitado sem Enter também conta ao salvar;
 * - a marcação "É de emergência" respeita o mínimo de 2 (C7501 no banco).
 *
 * C-91: os erros só aparecem depois de tentar salvar, mas daí em diante cada campo
 * acusado acompanha a digitação — some quando fica válido, sem salvar de novo.
 */
import { useState, type FormEvent } from 'react';
import {
  normalizarFuncoes,
  validarContato,
  type CampoContato,
  type ContatoEntrada,
} from '@/domain/contatos/validacao';
import {
  houveAlteracao,
  motivoBloqueioDesativar,
  motivoBloqueioDesmarcarEmergencia,
  proximaOrdem,
  sugestoesDeFuncao,
  usuariosVinculaveis,
} from '@/domain/contatos/regras';
import type { ContatoGestao, UsuarioDoApp } from '@/services/datasources';
import { useDefinirContatoAtivo, useSalvarContato, useUsuariosDoApp } from './useContatosAdmin';

export type Confirmacao = 'salvar' | 'desativar' | null;

function entradaInicial(contato: ContatoGestao | null): ContatoEntrada {
  return {
    funcoes: [...(contato?.funcoes ?? [])],
    name: contato?.name ?? '',
    phone: contato?.phone ?? '',
    display: contato?.display ?? '',
    email: contato?.email ?? '',
    empresa: contato?.empresa ?? '',
    disponibilidade: contato?.disponibilidade ?? '',
    is_emergencia: contato?.is_emergencia ?? false,
    app_user_id: contato?.app_user_id ?? null,
  };
}

export function useContatoForm(contato: ContatoGestao | null, todos: readonly ContatoGestao[], onClose: () => void) {
  const [entrada, setEntrada] = useState(() => entradaInicial(contato));
  const [funcaoDigitada, setFuncaoDigitada] = useState('');
  // Campos acusados na última tentativa de salvar. A mensagem mostrada é a da
  // validação ATUAL (abaixo): o campo corrigido some, e esvaziar de novo volta a acusar.
  const [acusados, setAcusados] = useState<readonly CampoContato[]>([]);
  const [confirmacao, setConfirmacao] = useState<Confirmacao>(null);
  const salvar = useSalvarContato();
  const ativo = useDefinirContatoAtivo();
  const usuariosQ = useUsuariosDoApp(true);

  const todosUsuarios: readonly UsuarioDoApp[] = usuariosQ.data ?? [];
  const usuario = entrada.app_user_id ? todosUsuarios.find((u) => u.id === entrada.app_user_id) ?? null : null;
  // O texto digitado e ainda sem Enter também é uma função.
  const completa: ContatoEntrada = { ...entrada, funcoes: [...entrada.funcoes, funcaoDigitada] };
  const opcoes = { gravado: contato, usuario };
  const mudou = contato ? houveAlteracao(completa, contato, usuario) : true;
  // Algo que se perde ao fechar (C-91): na edição, o que mudou; no novo, o que já foi
  // preenchido.
  const preenchido = contato
    ? mudou
    : funcaoDigitada.trim() !== '' || entrada.funcoes.length > 0 || entrada.is_emergencia ||
      entrada.app_user_id !== null ||
      (['name', 'phone', 'display', 'email', 'empresa', 'disponibilidade'] as const).some((c) => entrada[c].trim() !== '');
  const atual = validarContato(completa, opcoes);
  const erros: Partial<Record<CampoContato, string>> = {};
  if (!atual.ok) for (const c of acusados) if (atual.erros[c]) erros[c] = atual.erros[c];

  function validar() {
    salvar.reset();
    ativo.reset();
    setAcusados(atual.ok ? [] : (Object.keys(atual.erros) as CampoContato[]));
    return atual.ok ? atual.valor : null;
  }

  return {
    entrada,
    erros,
    confirmacao,
    setConfirmacao,
    mudou,
    preenchido,
    vinculado: entrada.app_user_id !== null,
    usuarios: usuariosVinculaveis(todosUsuarios, todos, contato?.id ?? null),
    carregandoUsuarios: usuariosQ.isLoading,
    /** Falha ao listar os usuários: avisa, em vez de virar lista vazia calada. */
    erroUsuarios: usuariosQ.isError,
    sugestoesFuncao: sugestoesDeFuncao(todos),
    funcaoDigitada,
    setFuncaoDigitada,
    bloqueioDesativar: contato?.is_active ? motivoBloqueioDesativar(contato, todos) : null,
    bloqueioDesmarcar: contato && entrada.is_emergencia ? motivoBloqueioDesmarcarEmergencia(contato, todos) : null,
    ocupado: salvar.isPending || ativo.isPending,
    salvando: salvar.isPending,
    erro: salvar.error ?? ativo.error,

    alterar(campo: Exclude<CampoContato, 'funcoes'>, valor: string) {
      setEntrada((p) => ({ ...p, [campo]: valor }));
    },
    marcarEmergencia(marcado: boolean) {
      setEntrada((p) => ({ ...p, is_emergencia: marcado }));
    },
    vincular(id: string | null) {
      const u = id ? todosUsuarios.find((x) => x.id === id) : undefined;
      // Desvincular mantém o que estava na tela, agora editável.
      setEntrada((p) => (u ? { ...p, app_user_id: u.id, name: u.name, email: u.email ?? '' } : { ...p, app_user_id: null }));
    },
    adicionarFuncao() {
      setEntrada((p) => ({ ...p, funcoes: normalizarFuncoes([...p.funcoes, funcaoDigitada]) }));
      setFuncaoDigitada('');
    },
    removerFuncao(funcao: string) {
      setEntrada((p) => ({ ...p, funcoes: p.funcoes.filter((f) => f !== funcao) }));
    },

    enviar(e: FormEvent) {
      e.preventDefault();
      // Criar e salvar passam pelo popup "Sim/Não" (Gabriel, 14/10).
      if (validar()) setConfirmacao('salvar');
    },
    confirmarSalvar() {
      setConfirmacao(null);
      const valor = validar();
      if (!valor) return;
      if (!contato) {
        salvar.mutate({ id: null, dados: { ...valor, sort_order: proximaOrdem(todos) } }, { onSuccess: onClose });
        return;
      }
      salvar.mutate({ id: contato.id, dados: valor }, { onSuccess: onClose });
    },
    definirAtivo(novo: boolean) {
      setConfirmacao(null);
      if (!contato) return;
      salvar.reset();
      ativo.mutate({ id: contato.id, ativo: novo }, { onSuccess: onClose });
    },
  };
}

export type FormularioContato = ReturnType<typeof useContatoForm>;
